import type { ReactElement } from "react"
import { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import BatchPredictionOutlinedIcon from "@mui/icons-material/BatchPredictionOutlined"
import BoltIcon from "@mui/icons-material/Bolt"
import BubbleChartOutlinedIcon from "@mui/icons-material/BubbleChartOutlined"
import BuildIcon from "@mui/icons-material/Build"
import CalculateOutlinedIcon from "@mui/icons-material/CalculateOutlined"
import CodeIcon from "@mui/icons-material/Code"
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined"
import DescriptionIcon from "@mui/icons-material/Description"
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined"
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined"
import IntegrationInstructionsOutlinedIcon from "@mui/icons-material/IntegrationInstructionsOutlined"
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft"
import KeyboardDoubleArrowRightOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined"
import MediationOutlinedIcon from "@mui/icons-material/MediationOutlined"
import ModelTrainingIcon from "@mui/icons-material/ModelTraining"
import OnlinePredictionIcon from "@mui/icons-material/OnlinePredictionOutlined"
import OpenInNewIcon from "@mui/icons-material/OpenInNew"
import PolylineIcon from "@mui/icons-material/Polyline"
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined"
import ScoreOutlinedIcon from "@mui/icons-material/ScoreOutlined"
import SettingsIcon from "@mui/icons-material/Settings"
import SubjectIcon from "@mui/icons-material/Subject"
import TagIcon from "@mui/icons-material/Tag"
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined"
import TimelineIcon from "@mui/icons-material/Timeline"
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined"
import { Divider, Drawer, Grid, SvgIcon, Switch, useTheme as useMUITheme, useMediaQuery } from "@mui/material"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Hidden from "@mui/material/Hidden"
import List from "@mui/material/List"
import Skeleton from "@mui/material/Skeleton"
import SwipeableDrawer from "@mui/material/SwipeableDrawer"
import { Menu, MenuItem, Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import queryString from "query-string"

import { KonanAvatar } from "../../../components/core/Avatar"
import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { KonanLogo } from "../../../components/icons/KonanLogo"
import { ProjectType } from "../../../components/status/ProjectType"
import { useLogOut } from "../../../features/Auth/helpers"
import { useTheme } from "../../../hooks/UseTheme"
import { useThrottle } from "../../../hooks/useThrottle"
import { SidAPI } from "../../../services/SidAPI"
import type { RootState } from "../../../store/ReduxStore"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { getEnableClose, setEnableClose } from "../../../utils/SessionStorageHelpers"
import {
  getEnvVar,
  getHostnames,
  getInitialLetters,
  getPosition,
  isEnvVariableTruthy,
} from "../../../utils/genericHelpers"
import { DashboardMenuItem } from "./DashboardMenuItem"
import { LanguageSelect } from "./LanguageSelect"

import styles from "../Dashboard.module.scss"
import "./DashboardDrawer.module.scss"

type Props = {
  orgName: string | undefined
  mobileOpen: boolean
  handleDrawerToggle: () => void
}

const avatarSize = 24

export function DashboardDrawer(props: Readonly<Props>): ReactElement {
  const { mobileOpen, handleDrawerToggle, orgName } = props

  const [closeWarningDialogInfo, setCloseWarningDialogInfo] = useState<{
    isOpen: boolean
    actionType?: { name: "switch" | "navigate"; path?: string }
  }>({
    isOpen: false,
  })

  const { t } = useTranslation("dashboard", {
    keyPrefix: "drawer",
  })

  const [theme, setTheme] = useTheme()

  const MUITheme = useMUITheme()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canListTaglists = useIsPermitted("List taglists", permissions.konan, flattenedKonanPermissions)

  const queryParams = useMemo(() => queryString?.parse(location.search), [location.search])

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const currentModel = useSelector((state: RootState) => state.models.currentModel)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState<boolean>(
    localStorage.getItem("is_drawer_collapsed") === "true",
  )

  const isSmDown = useMediaQuery(MUITheme.breakpoints.down("sm"))

  const LogOut = useLogOut()

  const isDrawerCollapsedOnBigScreen = isDrawerCollapsed && !isSmDown

  // Default width for the main drawer
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const drawerWidth = isDrawerCollapsedOnBigScreen ? 65 : 215

  const open = Boolean(anchorEl)

  // handler to toggle open state of logout menu, (on/off)
  // setting anchor null here to close in case it was opened already
  const handleMenu = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  const firstName = useSelector((state: RootState) => state.auth.firstName)
  const lastName = useSelector((state: RootState) => state.auth.lastName)
  const userEmail = useSelector((state: RootState) => state.auth.email)

  const toggleDrawer =
    () =>
    (event: React.KeyboardEvent<HTMLElement>): void => {
      if (event && event.type === "keydown" && (event.key === "Tab" || event.key === "Shift")) {
        return
      }

      handleDrawerToggle()
    }

  // navigate to "/projects" if pathname includes "projects/" and not followed by anything other than "/"
  useEffect(() => {
    if (location.pathname.includes("/projects/") && !location.pathname.match(/^\/projects\/[^/]+/)) {
      navigate("/projects")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const handleListItemClick = (path: string): void => {
    if (getEnableClose()) {
      setCloseWarningDialogInfo({
        isOpen: true,
        actionType: { name: "navigate", path },
      })
    } else {
      navigate(path)
    }
  }

  const handleAcceptCloseDialog = (): void => {
    switch (closeWarningDialogInfo?.actionType?.name) {
      case "navigate":
        navigate(closeWarningDialogInfo?.actionType?.path as string, {
          replace: true,
        })
        setEnableClose("false")
        break

      case "switch":
        setTheme(theme === "dark" ? "light" : "dark")
        break

      default:
        break
    }

    setCloseWarningDialogInfo({ isOpen: false })
  }

  const handleSwitchChange = (): void => {
    if (getEnableClose()) {
      setCloseWarningDialogInfo({
        isOpen: true,
        actionType: { name: "switch" },
      })
    } else {
      setTheme(theme === "dark" ? "light" : "dark")
    }
  }

  const handleMainDrawerToggle = (): void => {
    setIsDrawerCollapsed(!isDrawerCollapsed)
    localStorage.setItem("is_drawer_collapsed", String(!isDrawerCollapsed))
  }

  const throttledToggle = useThrottle(handleMainDrawerToggle, 500)

  const BottomDrawerList = (
    <Fragment>
      <Hidden smDown>
        <MenuItem
          onClick={throttledToggle}
          key="Expand"
          className={`${isDrawerCollapsedOnBigScreen && styles.collapsedMenuButton}`}
        >
          <div className={styles.menuButtonContent}>
            {isDrawerCollapsedOnBigScreen ? (
              <KeyboardDoubleArrowRightOutlinedIcon fontSize="small" />
            ) : (
              <KeyboardDoubleArrowLeftIcon fontSize="small" />
            )}

            {!isDrawerCollapsedOnBigScreen && (
              <Typography variant="a" variantColor={2}>
                {t("collapseBtn")}
              </Typography>
            )}
          </div>
        </MenuItem>
      </Hidden>

      <Grid container item xs={12} className={styles.themeSwitchContainer}>
        <Tooltip title={isDrawerCollapsedOnBigScreen ? t("tooltips.switchTheme") : ""} placement="right">
          <Grid item className={styles.themeSwitch}>
            <Switch checked={theme === "dark"} onChange={handleSwitchChange} name="Theme_switch" color="primary" />
          </Grid>
        </Tooltip>

        {!isDrawerCollapsedOnBigScreen && (
          <Grid item alignSelf={"center"}>
            <Typography variant="a" variantColor={2}>
              {t(`theme.${theme}`)}
            </Typography>
          </Grid>
        )}
      </Grid>

      <a
        href={`https://${getHostnames().docs}/`}
        rel="noopener noreferrer"
        target="_blank"
        onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
        className={styles.documentationButton}
      >
        <MenuItem className={styles.documentationMenuItem} data-collapsed={isDrawerCollapsedOnBigScreen}>
          <div className={styles.menuButtonContent}>
            <DescriptionIcon fontSize="small" />

            {!isDrawerCollapsedOnBigScreen && (
              <Typography variant="a" variantColor={2}>
                {t("documentationBtn")}
              </Typography>
            )}
          </div>

          {!isDrawerCollapsedOnBigScreen && <OpenInNewIcon fontSize="small" />}
        </MenuItem>
      </a>

      <DashboardMenuItem
        key="Account"
        text={`${firstName} ${lastName}`}
        icon={
          <SvgIcon className={styles.avatar}>
            <KonanAvatar size={avatarSize} name={userEmail ?? "User"} />
          </SvgIcon>
        }
        onClick={handleMenu}
        collapsed={isDrawerCollapsedOnBigScreen}
      />

      <Menu
        className={styles.collapsedLogoutMenu}
        maxWidth={100}
        id="menu-appbar"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          onClick={async () => {
            await SidAPI.logOut({ all_devices: false }).catch(console.error)
            LogOut({ redirect: false })
          }}
        >
          {t("logoutBtn")}
        </MenuItem>
      </Menu>
    </Fragment>
  )

  const ModelRoutes = (): ReactElement => {
    const routes = [
      {
        text: t("modelRoutes.configurations"),
        isSelected: location.pathname.includes("models") && location.pathname.includes("configurations"),
        isHidden: currentProject?.type === "generic",
        onClick: () =>
          handleListItemClick(
            location.pathname
              .substring(0, getPosition(location.pathname, "/", 3))
              .concat(`/models/configurations?model_id=${currentModel?.uuid || searchParams.get("model_id")}`),
          ),
        icon: <BuildIcon fontSize="small" />,
      },
      {
        text: t("modelRoutes.classificationMetrics"),
        isSelected: location.pathname.includes("classification"),
        isHidden: currentProject?.type === "generic",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/models/classification"),
          ),
        icon: <BubbleChartOutlinedIcon fontSize="small" />,
      },
      {
        text: t("modelRoutes.dataDrift"),
        isSelected: location.pathname.includes("drift"),
        isHidden: false,
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/models/drift"),
          ),
        icon: <TimelineIcon fontSize="small" />,
      },
    ]

    return (
      <Fragment>
        <Divider className={styles.divider} />
        <List className={styles.list}>
          {routes.map(
            (route) =>
              !route.isHidden && (
                <DashboardMenuItem
                  key={route.text}
                  text={route.text}
                  icon={route.icon}
                  isSelected={route.isSelected}
                  onClick={route.onClick}
                  collapsed={isDrawerCollapsedOnBigScreen}
                />
              ),
          )}
        </List>
      </Fragment>
    )
  }

  const drawer = (): ReactElement => {
    const isKonanAssistantEnabled = isEnvVariableTruthy(String(getEnvVar("KONAN_ASSISTANT_ENABLED")))

    const orgRoutes = [
      {
        text: t("orgRoutes.models"),
        isSelected: location.pathname.includes("projects") && queryParams?.page === "model",
        isHidden: false,
        onClick: () => handleListItemClick("/projects?page=model"),
        icon: <BatchPredictionOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("orgRoutes.workflows"),
        isSelected: location.pathname.includes("projects") && queryParams?.page === "workflow",
        isHidden: false,
        onClick: () => handleListItemClick("/projects?page=workflow"),
        icon: <MediationOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("orgRoutes.registry"),
        isSelected: location.pathname.includes("kcr"),
        isHidden: false,
        onClick: () => handleListItemClick("/kcr"),
        icon: <DnsOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("orgRoutes.settings"),
        isSelected: location.pathname.includes("settings"),
        isHidden: false,
        onClick: () => handleListItemClick("/settings"),
        icon: <SettingsIcon fontSize="small" />,
        isPermitted: true,
      },
    ]

    const projectRoutes = [
      {
        text: t("projectRoutes.insights"),
        isSelected: location.pathname.includes("insights"),
        isHidden: false,
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/insights"),
          ),
        icon: <DashboardOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.liveDecisions"),
        isSelected: location.pathname.includes("decisions"),
        isHidden: false,
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/decisions"),
          ),
        icon: <BoltIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.dataFiles"),
        isSelected: location.pathname.includes("data-files"),
        isHidden: false,
        onClick: () =>
          handleListItemClick(
            location.pathname
              .substring(0, getPosition(location.pathname, "/", 3))
              .concat("/data-files?page=Data-files"),
          ),
        icon: <FolderOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.overview"),
        isSelected: location.pathname.includes("overview"),
        isHidden: currentProject?.category !== "model",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/overview"),
          ),
        icon: <DashboardOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.liveMonitoring"),
        isSelected: location.pathname.includes("requests") && !location.pathname.includes("models"),
        isHidden: currentProject?.category !== "model",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/requests"),
          ),
        icon: <OnlinePredictionIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.models"),
        isSelected: location.pathname.includes("decision-engines"),
        isHidden: currentProject?.category !== "model",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/decision-engines"),
          ),
        icon: <BatchPredictionOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.workflows"),
        isSelected: location.pathname.includes("workflows"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/workflows"),
          ),
        icon: <MediationOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.konanAssistant"),
        isSelected: location.pathname.includes("konan-assistant"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/konan-assistant"),
          ),
        icon: <TipsAndUpdatesOutlinedIcon fontSize="small" />,
        isPermitted: isKonanAssistantEnabled,
      },
      {
        text: t("projectRoutes.calculators"),
        isSelected: location.pathname.includes("calculators"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/calculators"),
          ),
        icon: <CalculateOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.rulesets"),
        isSelected: location.pathname.includes("Rulesets"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/Rulesets"),
          ),
        icon: <RuleFolderOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.taglists"),
        isSelected: location.pathname.includes("Taglists"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/Taglists"),
          ),
        icon: <TagIcon fontSize="small" />,
        isPermitted: canListTaglists,
      },
      {
        text: t("projectRoutes.scorecards"),
        isSelected: location.pathname.includes("Scorecards"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/Scorecards"),
          ),
        icon: <ScoreOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.programs"),
        isSelected: location.pathname.includes("Programs"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/Programs"),
          ),
        icon: <SubjectIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: "Marketplace",
        isSelected: location.pathname.includes("Marketplace"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/Marketplace"),
          ),
        icon: <PolylineIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.scripts"),
        isSelected: location.pathname.includes("Scripts"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/Scripts"),
          ),
        icon: <CodeIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.simulationReports"),
        isSelected: location.pathname.includes("SimulationReports"),
        isHidden: currentProject?.category !== "workflow",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/SimulationReports"),
          ),
        icon: <TextSnippetOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.modelTraining"),
        isSelected: location.pathname.includes("model-training"),
        isHidden: currentProject?.category !== "model",
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/model-training"),
          ),
        icon: <ModelTrainingIcon fontSize="small" />,
        isPermitted: true,
      },
      {
        text: t("projectRoutes.integration"),
        isSelected: location.pathname.includes("integration"),
        isHidden: false,
        onClick: () =>
          handleListItemClick(
            location.pathname.substring(0, getPosition(location.pathname, "/", 3)).concat("/integration"),
          ),
        icon: <IntegrationInstructionsOutlinedIcon fontSize="small" />,
        isPermitted: true,
      },
    ]

    return (
      <Box width="auto" onClick={handleDrawerToggle} onKeyDown={toggleDrawer}>
        <div className={styles.newOrganizationContainer}>
          {orgName ? (
            isDrawerCollapsedOnBigScreen ? (
              /* TODO: add default grey variant to it later  */
              <Tooltip placement="right" title={orgName}>
                <Tag className={styles.grayTag} size="small">
                  {getInitialLetters(orgName)}
                </Tag>
              </Tooltip>
            ) : (
              <Typography variant="label-bold" noWrap>
                {orgName}
              </Typography>
            )
          ) : (
            <Skeleton animation="wave" width={isDrawerCollapsedOnBigScreen ? 50 : 150} />
          )}
        </div>

        <Grid container justifyContent="space-between" paddingX={isDrawerCollapsedOnBigScreen ? 0 : 2}>
          <Button onClick={() => navigate("/")} className={styles.logo}>
            <KonanLogo
              logoWidth={isDrawerCollapsedOnBigScreen ? "unset" : 96}
              isDrawerCollapsed={isDrawerCollapsedOnBigScreen}
              isDrawerView={!isSmDown}
            />
          </Button>
          <Grid item>
            <LanguageSelect />
          </Grid>
        </Grid>

        <Divider className={styles.bottomDivider} />
        <List className={styles.list}>
          {orgRoutes.map(
            (route) =>
              !route.isHidden &&
              route.isPermitted && (
                <DashboardMenuItem
                  key={route.text}
                  text={route.text}
                  icon={route.icon}
                  isSelected={route.isSelected}
                  onClick={route.onClick}
                  collapsed={isDrawerCollapsedOnBigScreen}
                />
              ),
          )}
        </List>

        <Divider className={styles.divider} />

        {location.pathname.includes("/projects/") &&
          location.pathname.match(/^\/projects\/[^/]+/) &&
          !location.pathname.includes("new") && (
            <Fragment>
              {!isDrawerCollapsedOnBigScreen && (
                <div className={styles.organizationContainer}>
                  <Typography variant="h3-bold" noWrap>
                    {currentProject?.category ? (
                      currentProject?.category.charAt(0).toUpperCase() + currentProject?.category.slice(1)
                    ) : (
                      <Skeleton animation="wave" width={150} />
                    )}
                  </Typography>
                </div>
              )}

              {currentProject?.category === "model" && !isDrawerCollapsedOnBigScreen && (
                <ProjectType
                  type={currentProject?.type}
                  margin={MUITheme.spacing(0, 0, 0, 2)}
                  size="default"
                  skeletonWidth={100}
                />
              )}

              <List className={styles.list}>
                {projectRoutes.map(
                  (route) =>
                    !route.isHidden &&
                    route.isPermitted && (
                      <DashboardMenuItem
                        key={route.text}
                        text={route.text}
                        icon={route.icon}
                        isSelected={route.isSelected}
                        onClick={route.onClick}
                        collapsed={isDrawerCollapsedOnBigScreen}
                      />
                    ),
                )}
              </List>

              {currentModel?.uuid && <ModelRoutes />}
              <Hidden smUp>
                <Divider className={styles.divider} />
              </Hidden>
            </Fragment>
          )}

        <div className={isSmDown ? styles.list : styles.bottomListItems}>
          <Hidden smDown>
            <Divider className={styles.divider} />
          </Hidden>

          <List>{BottomDrawerList}</List>
        </div>
      </Box>
    )
  }

  return (
    <Fragment>
      {closeWarningDialogInfo?.isOpen && (
        <BaseSimpleDialog
          onClose={() => setCloseWarningDialogInfo({ isOpen: false })}
          open
          mode="close-while-edit"
          isLoading={false}
          name=""
          onAccept={handleAcceptCloseDialog}
        />
      )}

      <nav
        className={isDrawerCollapsedOnBigScreen ? styles.collapsedNav : !isSmDown ? styles.nav : undefined}
        aria-label="mailbox folders"
      >
        <Hidden smUp implementation="js">
          <SwipeableDrawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            onOpen={handleDrawerToggle}
            classes={{
              paper: styles.drawerPaper,
            }}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
          >
            {drawer()}
          </SwipeableDrawer>
        </Hidden>
        <Hidden smDown implementation="js">
          <Drawer
            classes={{
              paper: isDrawerCollapsedOnBigScreen ? styles.collapsedDrawerPaper : styles.drawerPaper,
            }}
            variant="permanent"
            open={!isDrawerCollapsedOnBigScreen}
            onClose={handleMainDrawerToggle}
          >
            <div className={styles.scrollableDrawer}>{drawer()}</div>
          </Drawer>
        </Hidden>
      </nav>
    </Fragment>
  )
}
